import { useEffect } from 'react';

declare global {
  interface Window {
    $: any;
    jQuery: any;
    WOW: any;
    M: any;
    YT: any;
    onYouTubeIframeAPIReady: () => void;
  }
}

export function usePageInit() {
  useEffect(() => {
    let overlayFallbackTimer: number | null = null;

    // Wait for jQuery and other libraries to be loaded
    const initScripts = () => {
      if (typeof window.$ === 'undefined' || typeof window.M === 'undefined') {
        setTimeout(initScripts, 100);
        return;
      }

      const $ = window.$;

      // Preloader - 既に非表示の場合はスキップ
      const preloader = $('#preloader');
      if (preloader.length > 0 && preloader.is(':visible')) {
        preloader.delay(1000).fadeOut('fast');
      }
      
      // Loading indicator
      const loading = $('.loading');
      if (loading.length > 0 && loading.is(':visible')) {
        loading.delay(1000).fadeOut('fast');
      }

      // 遷移アニメーション完了後にオーバーレイ（.loading / #preloader）を確実に非表示にする（スマホでタップするまで消えない問題対策）
      const hideOverlays = () => {
        const preloaderEl = document.getElementById('preloader');
        if (preloaderEl) preloaderEl.style.display = 'none';
        document.querySelectorAll('.loading').forEach((el) => {
          (el as HTMLElement).style.display = 'none';
        });
      };

      // フォールバック: キューが動かない環境（例: スマホのバックグラウンド復帰）でも遷移後は消す
      const TRANSITION_END_MS = 2200;
      overlayFallbackTimer = window.setTimeout(hideOverlays, TRANSITION_END_MS) as unknown as number;

      // Page transition
      $('.transition-page').addClass('page-fadeUp-transition-enter').delay(1000).queue(function (this: any) {
        $(this)
          .removeClass('page-fadeUp-transition-enter')
          .addClass('page-fadeUp-transition-enter-active')
          .dequeue()
          .delay(500)
          .queue(function (this: any) {
            if (overlayFallbackTimer != null) {
              window.clearTimeout(overlayFallbackTimer);
              overlayFallbackTimer = null;
            }
            $(this)
              .removeClass('page-fadeUp-transition-enter-active')
              .addClass('page-fadeUp-transition-exit')
              .dequeue();
            // 遷移アニメーション完了後、オーバーレイを自動で非表示（スマホでも確実に消す）
            hideOverlays();
          });
      });

      // Scroll navigation - only initialize if elements exist
      if ($.fn.navScroll) {
        // Fixed header offset for anchor scrolling (prevents headings being hidden under the header)
        const headerHeight = Number($('#header').outerHeight() || 0);
        // Bring section headings closer to the header by reducing the offset a bit.
        // (If it's too tight, tweak this value.)
        const TUNE_PX = 20;
        const scrollOffset = Math.max(0, Math.ceil(headerHeight) - TUNE_PX);
        document.documentElement.style.setProperty('--scroll-offset', `${scrollOffset}px`);

        const $scrollnav = $('.scrollnav');
        if ($scrollnav.length > 0) {
          try {
            // Check if all anchor links have valid targets
            let hasValidTargets = true;
            $scrollnav.find('a[href^="#"]').each(function (this: HTMLElement) {
              const href = $(this).attr('href');
              if (href) {
                const targetId = href.substring(1);
                if (targetId && $(`#${targetId}`).length === 0) {
                  hasValidTargets = false;
                  return false; // break loop
                }
              }
            });

            if (hasValidTargets) {
              $scrollnav.navScroll({
                scrollSpy: true,
                activeParent: true,
                activeClassName: 'current',
                navHeight: scrollOffset,
              });
            }
          } catch (e) {
            console.warn('Error initializing navScroll:', e);
          }
        }
      }

      // WOW animations - destroy existing instance first
      if (window.WOW) {
        // Destroy existing WOW instance if any
        const existingWOW = (window as any).wowInstance;
        if (existingWOW) {
          try {
            existingWOW.stop();
            (window as any).wowInstance = null;
          } catch (e) {
            // Ignore errors
          }
        }
        // Create new WOW instance
        const wow = new window.WOW({
          boxClass: 'wow',
          animateClass: 'animated',
          offset: 0,
          mobile: true,
          live: true,
        });
        wow.init();
        (window as any).wowInstance = wow;
        
        // Force refresh after a short delay to ensure elements are rendered
        setTimeout(() => {
          if ((window as any).wowInstance) {
            try {
              (window as any).wowInstance.sync();
            } catch (e) {
              // Ignore errors
            }
          }
        }, 100);
      }

      // Parallax
      if ($.fn.enllax) {
        $('#feature_parallax').enllax();
      }

      // Accordion
      if ($.fn.collapsible) {
        $('.collapsible').collapsible();
      }

      // Select
      if ($.fn.formSelect) {
        $('.select').formSelect();
      }

      // Tooltip
      if ($.fn.tooltip) {
        $('.tooltipped').tooltip();
      }

      // Dropdown
      if ($.fn.dropdown) {
        $('.dropdown-trigger').dropdown({
          closeOnClick: false,
          alignment: 'left',
        });
      }

      // Sidenav for mobile menu
      if ($.fn.sidenav) {
        let isOpen = false;
        const $header = $('#header');
        const $menu = $('#mobile_menu');
        const $slideMenu = $('#slide-menu');

        $('#mobile_menu').off('click').on('click', function (this: any) {
          isOpen = !isOpen;
          if (isOpen) {
            $('.sidenav').sidenav('open');
          } else {
            $('.sidenav').sidenav('close');
          }
        });

        $('.sidenav').sidenav({
          onOpenStart: function () {
            isOpen = true;
            $header.addClass('open-drawer');
            $menu.addClass('is-active');
            $slideMenu.addClass('menu-open');
          },
          onCloseEnd: function () {
            isOpen = false;
            $header.removeClass('open-drawer');
            $menu.removeClass('is-active');
            $slideMenu.removeClass('menu-open');
          },
        });
      }

      // Theme switcher
      const darkMode = localStorage.getItem('luxiDarkMode') || 'false';
      if (darkMode === 'true') {
        $('#app').removeClass('theme--light').addClass('theme--dark');
        $('#theme_switcher').prop('checked', true);
      }

      $('#theme_switcher').off('change').on('change', function (this: any) {
        if ($(this).is(':checked')) {
          localStorage.setItem('luxiDarkMode', 'true');
          $('#app').removeClass('theme--light').addClass('theme--dark');
        } else {
          localStorage.setItem('luxiDarkMode', 'false');
          $('#app').removeClass('theme--dark').addClass('theme--light');
        }
      });

      // Form validation (legacy)
      // NOTE: #contact_form はReact側で onSubmit(fetch) を持つため、ここで jQuery validate により submit を止めると
      // APIリクエストが飛ばなくなる。React側のバリデーション/送信に委ねる。

      // Scroll handlers
      const $header = $('#header');
      const $pageNav = $('#page_nav');
      let sticky = 0;

      if ($('#header').length > 0) {
        const headerElement = document.getElementById('header');
        if (headerElement) {
          sticky = headerElement.offsetTop + 100;
        }
      }

      function fixedNav() {
        if (window.pageYOffset > sticky) {
          $header.addClass('fixed');
        } else {
          $header.removeClass('fixed');
        }
      }

      function fixedFabNav() {
        if (window.pageYOffset > 500) {
          $pageNav.addClass('show');
        } else {
          $pageNav.removeClass('show');
        }
      }

      // Remove existing scroll handler and add new one
      window.removeEventListener('scroll', fixedNav);
      window.removeEventListener('scroll', fixedFabNav);
      window.addEventListener('scroll', () => {
        fixedNav();
        fixedFabNav();
      });

      // Counter scroll animation
      // Initialize scrollzip functions
      if (!$.fn.scrollzipInit) {
        $.fn.scrollzipInit = function (this: any) {
          if ($('#scrollzipPoint').length === 0) {
            $('body').prepend("<div style='position:fixed;top:0px;left:0px;width:0;height:0;' id='scrollzipPoint'></div>");
          }
          return this;
        };
      }

      if (!$.fn.rollerInit) {
        $.fn.rollerInit = function (this: any) {
          let i = 0;
          $('.numscroller').each(function (this: HTMLElement) {
            i++;
            $(this).attr('data-slno', i.toString());
            $(this).addClass('roller-title-number-' + i);
          });
          return this;
        };
      }

      if (!$.fn.scrollzip) {
        $.fn.scrollzip = function (this: any, options: any) {
          const settings = $.extend(
            {
              showFunction: null,
              hideFunction: null,
              showShift: 0,
              wholeVisible: false,
              hideShift: 0,
            },
            options,
          );
          return this.each(function (this: HTMLElement) {
            const $this = $(this);
            $this.addClass('scrollzip');
            if ($.isFunction(settings.showFunction) && $('#scrollzipPoint').length > 0) {
              const scrollzipPointOffset = $('#scrollzipPoint').offset();
              const thisOffset = $this.offset();
              const windowHeight = $(window).outerHeight() || 0;
              const thisHeight = $this.outerHeight() || 0;
              
              if (
                !$this.hasClass('isShown') &&
                scrollzipPointOffset &&
                thisOffset &&
                windowHeight + scrollzipPointOffset.top - settings.showShift >
                  thisOffset.top + (settings.wholeVisible ? thisHeight : 0) &&
                scrollzipPointOffset.top + (settings.wholeVisible ? thisHeight : 0) <
                  thisHeight + thisOffset.top - settings.showShift
              ) {
                $this.addClass('isShown');
                settings.showFunction.call(this);
              }
            }
            if ($.isFunction(settings.hideFunction)) {
              const scrollzipPointOffset = $('#scrollzipPoint').offset();
              const thisOffset = $this.offset();
              const windowHeight = $(window).outerHeight() || 0;
              const thisHeight = $this.outerHeight() || 0;
              
              if (
                $this.hasClass('isShown') &&
                scrollzipPointOffset &&
                thisOffset &&
                (windowHeight + scrollzipPointOffset.top - settings.hideShift <
                  thisOffset.top + (settings.wholeVisible ? thisHeight : 0) ||
                  scrollzipPointOffset.top + (settings.wholeVisible ? thisHeight : 0) >
                    thisHeight + thisOffset.top - settings.hideShift)
              ) {
                $this.removeClass('isShown');
                settings.hideFunction.call(this);
              }
            }
            return this;
          });
        };
      }

      // Define numberRoller and numberRoll functions globally
      if (typeof (window as any).numberRoller === 'undefined') {
        (window as any).numberRoller = function (slno: string) {
          const $element = $('.roller-title-number-' + slno);
          if ($element.length === 0) return;
          
          const min = parseInt($element.attr('data-min') || '0');
          const max = parseInt($element.attr('data-max') || '0');
          const timediff = parseInt($element.attr('data-delay') || '0');
          const increment = parseInt($element.attr('data-increment') || '0');
          const numdiff = max - min;
          const timeout = numdiff > 0 ? (timediff * 1000) / numdiff : 0;
          
          (window as any).numberRoll(slno, min, max, increment, timeout);
        };

        (window as any).numberRoll = function (slno: string, min: number, max: number, increment: number, timeout: number) {
          if (min <= max) {
            $('.roller-title-number-' + slno).html(min.toString());
            min = min + increment;
            setTimeout(function () {
              (window as any).numberRoll(slno, min, max, increment, timeout);
            }, timeout);
          } else {
            $('.roller-title-number-' + slno).html(max.toString());
          }
        };
      }

      // Initialize counter
      $(document).scrollzipInit();
      $(document).rollerInit();

      // Handle scroll events
      const handleCounterScroll = () => {
        $('.numscroller').scrollzip({
          showFunction: function (this: HTMLElement) {
            const slno = $(this).attr('data-slno');
            if (slno && typeof (window as any).numberRoller === 'function') {
              (window as any).numberRoller(slno);
            }
          },
          wholeVisible: false,
        });
      };

      // Trigger on load and scroll
      $(window).on('load', handleCounterScroll);
      $(window).on('scroll resize', handleCounterScroll);
      
      // Also trigger immediately if already loaded
      if (document.readyState === 'complete') {
        handleCounterScroll();
      }

      // Showcase slider
      const $carouselCenter = $('#slider_center');
      const $carouselSide = $('#slider_side');
      const $mobileDot = $('#showcase_mobile_nav li button');

      if ($.fn.slick && $carouselCenter.length > 0 && $carouselSide.length > 0) {
        // Check if carousels have child elements
        const centerHasItems = $carouselCenter.find('.item').length > 0;
        const sideHasItems = $carouselSide.find('.item').length > 0;

        if (centerHasItems && sideHasItems) {
          // Destroy existing sliders safely
          try {
            if ($carouselCenter.hasClass('slick-initialized')) {
              $carouselCenter.slick('unslick');
            }
          } catch (e) {
            console.warn('Error destroying center slider:', e);
          }
          
          try {
            if ($carouselSide.hasClass('slick-initialized')) {
              $carouselSide.slick('unslick');
            }
          } catch (e) {
            console.warn('Error destroying side slider:', e);
          }

          // Initialize sliders
          try {
            $carouselCenter.slick({
              dots: false,
              infinite: true,
              speed: 500,
              fade: true,
              arrows: false,
              asNavFor: '.slider-side',
            });

            $carouselSide.slick({
              autoplay: true,
              autoplaySpeed: 5000,
              dots: false,
              infinite: true,
              speed: 500,
              centerMode: true,
              centerPadding: '10px',
              slidesToShow: 5,
              arrows: false,
              asNavFor: '.slider-center',
              responsive: [
                {
                  breakpoint: 800,
                  settings: {
                    slidesToShow: 3,
                    slidesToScroll: 1,
                  },
                },
              ],
            });

            $mobileDot.off('click').on('click', function (this: any) {
              const index = $(this).data('index');
              $('#showcase_mobile_nav li').removeClass('active');
              $(this).parent().addClass('active');
              if ($carouselCenter.hasClass('slick-initialized')) {
                $carouselCenter.slick('slickGoTo', index - 1);
              }
            });

            $carouselSide.on('afterChange', function (_event: any, _slick: any, currentSlide: number) {
              const active = currentSlide + 1;
              $('#showcase_mobile_nav li').removeClass('active');
              $(`#showcase_mobile_nav li:nth-child(${active})`).addClass('active');
            });
          } catch (e) {
            console.warn('Error initializing showcase sliders:', e);
          }
        }
      }

      // Testimonials slider
      const $carouselText = $('#slider_text');
      const $carouselAvatar = $('#slider_avatar');

      if ($.fn.slick && $carouselText.length > 0 && $carouselAvatar.length > 0) {
        // Check if carousels have child elements
        const textHasItems = $carouselText.find('.item').length > 0;
        const avatarHasItems = $carouselAvatar.find('.item').length > 0;

        if (textHasItems && avatarHasItems) {
          // Destroy existing sliders safely
          try {
            if ($carouselText.hasClass('slick-initialized')) {
              $carouselText.slick('unslick');
            }
          } catch (e) {
            console.warn('Error destroying text slider:', e);
          }
          
          try {
            if ($carouselAvatar.hasClass('slick-initialized')) {
              $carouselAvatar.slick('unslick');
            }
          } catch (e) {
            console.warn('Error destroying avatar slider:', e);
          }

          // Initialize sliders
          try {
            $carouselText.slick({
              dots: false,
              infinite: true,
              speed: 500,
              fade: true,
              arrows: false,
              asNavFor: '.slider-avatar',
            });

            $carouselAvatar.slick({
              dots: false,
              infinite: true,
              speed: 500,
              focusOnSelect: true,
              autoplay: true,
              centerPadding: '2px',
              centerMode: true,
              autoplaySpeed: 5000,
              slidesToShow: 7,
              pauseOnHover: false,
              arrows: false,
              asNavFor: '.slider-text',
              responsive: [
                {
                  breakpoint: 600,
                  settings: {
                    slidesToShow: 3,
                    slidesToScroll: 1,
                  },
                },
              ],
            });
          } catch (e) {
            console.warn('Error initializing testimonials sliders:', e);
          }
        }
      }

      // Video modal
      if ($.fn.modal) {
        $('.modal').modal({
          onOpenEnd: function () {
            if (typeof (window as any).playVideo === 'function') {
              (window as any).playVideo();
            }
          },
          onCloseEnd: function () {
            if (typeof (window as any).stopVideo === 'function') {
              (window as any).stopVideo();
            }
          },
        });

        $('.modal .modal-close').off('click').on('click', function () {
          if (typeof (window as any).stopVideo === 'function') {
            (window as any).stopVideo();
          }
        });
      }
    };

    // Start initialization
    initScripts();

    // Cleanup function
    return () => {
      if (overlayFallbackTimer != null) {
        window.clearTimeout(overlayFallbackTimer);
      }
      // Cleanup if needed
      if (typeof window.$ !== 'undefined') {
        const $ = window.$;
        
        // Remove event listeners
        $(window).off('scroll');
        $(window).off('load');
        $(window).off('resize');
        
        // Destroy WOW instance
        if ((window as any).wowInstance) {
          try {
            (window as any).wowInstance.sync();
            (window as any).wowInstance = null;
          } catch (error) {
            console.warn('Error cleaning up WOW:', error);
          }
        }
        
        // Destroy sliders
        if ($.fn.slick) {
          try {
            if ($('#slider_center').hasClass('slick-initialized')) {
              $('#slider_center').slick('unslick');
            }
            if ($('#slider_side').hasClass('slick-initialized')) {
              $('#slider_side').slick('unslick');
            }
            if ($('#slider_text').hasClass('slick-initialized')) {
              $('#slider_text').slick('unslick');
            }
            if ($('#slider_avatar').hasClass('slick-initialized')) {
              $('#slider_avatar').slick('unslick');
            }
          } catch (error) {
            console.warn('Error cleaning up sliders:', error);
          }
        }
        
        // Destroy tooltips
        if ($.fn.tooltip) {
          try {
            $('.tooltipped').tooltip('destroy');
          } catch (error) {
            console.warn('Error cleaning up tooltips:', error);
          }
        }
        
        // Destroy modals
        if ($.fn.modal) {
          try {
            $('.modal').modal('close');
          } catch (error) {
            console.warn('Error cleaning up modals:', error);
          }
        }
        
        // Destroy sidenav
        if ($.fn.sidenav) {
          try {
            $('.sidenav').sidenav('destroy');
          } catch (error) {
            console.warn('Error cleaning up sidenav:', error);
          }
        }
        
        // Remove event handlers
        $('#mobile_menu').off('click');
        $('#theme_switcher').off('change');
        $('.modal .modal-close').off('click');
        $('#showcase_mobile_nav li button').off('click');
      }
    };
  }, []);
}

